"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Circle, Wallet } from "lucide-react";
import { AeraButton } from "@aera/ui";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import { resolveOwnerUserId } from "@/lib/pendingOrders";
import {
  buildReceivableHistoryEntries,
  buildReceivableHistorySummary,
  fetchPendingItemHistory,
  type ReceivableHistoryEntry,
} from "@/app/artifacts/table/receivableHistory";
import {
  HistoryActorAvatar,
  HistoryAmountText,
  historyAmountToneClass,
  MoneyUndoConfirmSheet,
  parseHistoryStatusDetailLine,
  resolveReceivableHistoryAmountTone,
  StatusBadge,
} from "@aera/data-table";
import { showToast } from "@/app/components/ui/Toast";
import { postUndoCorrection, fetchUndoImpact, formatUndoBlockMessage, type UndoImpact } from "@/lib/moneyOperations";
import {
  clearLatestMoneyUndo,
  readLatestMoneyUndo,
} from "@/lib/latestMoneyUndo";

const RECEIVABLE_AMOUNT_TONE = "receivable" as const;

function splitStatuses(value: string): string[] {
  const parts = value
    .split(/[•·|,]/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [value.trim()].filter(Boolean);
}

function CurrentSummaryCard({ item }: { item: ReceivableListItem }) {
  const summary = useMemo(() => buildReceivableHistorySummary(item), [item]);

  return (
    <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated)">
      <div className="flex items-center gap-2 border-b border-(--aera-dt-border-soft) bg-aera-white px-4 py-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-(--aera-dt-row-selected-surface) text-(--aera-dt-accent-hover)">
          <Wallet className="h-4 w-4" aria-hidden />
        </div>
        <h3 className="text-sm font-bold text-aera-charcoal">{summary.headline}</h3>
      </div>

      <div className="space-y-3 px-4 py-4">
        {summary.rows.map((row) => (
          <div
            key={row.label}
            className="flex items-start justify-between gap-4"
          >
            <span className="shrink-0 text-xs font-semibold text-aera-lightslate">
              {row.label}
            </span>
            {row.label === "สถานะ" ? (
              <div className="flex flex-wrap justify-end gap-1.5">
                {splitStatuses(row.value).map((status) => (
                  <StatusBadge key={status} status={status} />
                ))}
              </div>
            ) : (
              <span
                className={`text-right text-sm leading-snug ${
                  row.emphasize
                    ? historyAmountToneClass(RECEIVABLE_AMOUNT_TONE)
                    : "font-medium text-aera-gray"
                }`}
              >
                {row.value}
              </span>
            )}
          </div>
        ))}

        {summary.explanation.trim() ? (
          <p className="rounded-xl bg-aera-offwhite px-3 py-2.5 text-xs leading-relaxed text-aera-gray">
            {summary.explanation}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function markEntryNotUndoable(entry: ReceivableHistoryEntry): ReceivableHistoryEntry {
  return {
    ...entry,
    reversible: false,
    availableActions: (entry.availableActions || []).filter(
      (action) => action !== "undo_latest",
    ),
  };
}

function HistoryEntryRow({
  entry,
  isLast,
  canUndo,
  undoBusy,
  onRequestUndo,
}: {
  entry: ReceivableHistoryEntry;
  isLast: boolean;
  canUndo: boolean;
  undoBusy: boolean;
  onRequestUndo: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const details = entry.details ?? [];
  const hasDetails = details.length > 0;
  const amountTone = resolveReceivableHistoryAmountTone(entry);
  const showActor = Boolean(entry.actorId?.trim() && entry.actorId !== "—");
  const description = entry.description.trim();
  const title = entry.title.trim();
  const headline =
    description && description !== title ? description : title || description;

  return (
    <div className="flex gap-3">
      <div className="flex w-4 shrink-0 flex-col items-center pt-1.5">
        <Circle
          className={`h-2 w-2 fill-current ${
            entry.isLatest ? "text-aera-amber" : "text-aera-lightgray"
          }`}
          aria-hidden
        />
        {!isLast ? (
          <div className="mt-1 w-px flex-1 bg-aera-lightgray/80" aria-hidden />
        ) : null}
      </div>

      <div className={`min-w-0 flex-1 ${isLast ? "pb-1" : "pb-4"}`}>
        <p className="text-sm font-semibold leading-snug text-aera-charcoal">
          <HistoryAmountText text={headline} tone={amountTone} />
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-aera-lightslate">
          <span className="tabular-nums">{entry.occurredAt}</span>
          {showActor ? (
            <>
              <span aria-hidden>·</span>
              <HistoryActorAvatar
                actorId={entry.actorId}
                actorLabel={entry.actorName}
              />
            </>
          ) : null}
        </div>

        {canUndo ? (
          <div className="mt-2">
            <AeraButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={undoBusy}
              onClick={onRequestUndo}
            >
              {undoBusy ? "กำลังโหลด…" : "ย้อนกลับการแก้ไขล่าสุด"}
            </AeraButton>
          </div>
        ) : null}

        {hasDetails ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="mt-1.5 flex cursor-pointer items-center gap-0.5 text-[11px] font-medium text-aera-lightslate transition-colors hover:text-aera-charcoal"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
              aria-hidden
            />
            {expanded ? "ซ่อนรายละเอียด" : "รายละเอียด"}
          </button>
        ) : null}

        {expanded ? (
          <ul className="mt-1.5 space-y-0.5 pl-0.5">
            {details.map((line) => {
              const parsed = parseHistoryStatusDetailLine(line);
              return (
                <li
                  key={line}
                  className="text-[11px] leading-relaxed text-aera-gray before:mr-1.5 before:text-aera-lightslate before:content-['•']"
                >
                  {parsed ? (
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <span>{parsed.prefix}:</span>
                      {parsed.statuses.map((status) => (
                        <StatusBadge key={`${line}:${status}`} status={status} />
                      ))}
                    </span>
                  ) : (
                    <HistoryAmountText text={line} tone={amountTone} />
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export function ReceivableHistoryTab({
  item,
  ownerUserId = null,
  workspaceId = null,
  refreshKey = 0,
  onUndone,
  readOnly = false,
}: {
  item: ReceivableListItem;
  ownerUserId?: string | null;
  workspaceId?: string | null;
  /** Bump to reload history after edits outside this tab. */
  refreshKey?: number;
  onUndone?: () => void | Promise<void>;
  /** Role cannot write — hide “ย้อนกลับการแก้ไขล่าสุด”. */
  readOnly?: boolean;
}) {
  const [entries, setEntries] = useState<ReceivableHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [undoPreview, setUndoPreview] = useState<{
    entry: ReceivableHistoryEntry;
    impact: UndoImpact;
  } | null>(null);
  const [undoPreviewLoading, setUndoPreviewLoading] = useState(false);
  const [undoBlockedIds, setUndoBlockedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    // Only reset when switching items — keep blocked ids across history refresh
    // so an already-undone op cannot regain the button if the API is briefly stale.
    setUndoBlockedIds(new Set());
  }, [item.key, item.mergeKey]);

  useEffect(() => {
    const owner = ownerUserId?.trim() || resolveOwnerUserId();
    if (!owner) {
      setEntries(buildReceivableHistoryEntries(item));
      setLoadError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    void fetchPendingItemHistory(item, owner)
      .then((rows) => {
        if (cancelled) return;
        setEntries(rows);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setEntries([]);
        setLoadError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ownerUserId, item.key, item.mergeKey, refreshKey]);

  // If history already reflects this op as undone / no longer latest, drop the banner undo.
  useEffect(() => {
    const remembered = readLatestMoneyUndo();
    if (!remembered || entries.length === 0) return;
    const known = entries.some((entry) => entry.id === remembered.operationId);
    if (!known) return;
    const stillUndoable = entries.some(
      (entry) =>
        entry.id === remembered.operationId &&
        entry.isLatest &&
        (entry.reversible === true ||
          Boolean(entry.availableActions?.includes("undo_latest"))),
    );
    if (!stillUndoable) {
      clearLatestMoneyUndo();
    }
  }, [entries]);

  const hideUndoForEntry = (entryId: string) => {
    setUndoBlockedIds((prev) => {
      const next = new Set(prev);
      next.add(entryId);
      return next;
    });
    setEntries((rows) =>
      rows.map((row) =>
        row.id === entryId ? markEntryNotUndoable(row) : row,
      ),
    );
    // Always drop the sticky "ธุรกรรมล่าสุด" bar — don't wait for impact poll /
    // don't require exact operationId match (toast id can drift from history id).
    clearLatestMoneyUndo();
  };

  const handleRequestUndo = async (entry: ReceivableHistoryEntry) => {
    const owner = ownerUserId?.trim() || resolveOwnerUserId();
    if (!owner || !workspaceId?.trim()) {
      setUndoError("ไม่พบ workspace สำหรับย้อนกลับ");
      return;
    }
    setUndoPreviewLoading(true);
    setUndoBusy(true);
    setUndoError(null);
    try {
      const impact = await fetchUndoImpact({
        ownerUserId: owner,
        targetOperationId: entry.id,
      });
      if (impact.blocked || !impact.ok) {
        // Already undone / not undoable — hide the button immediately.
        // Do not leave the button visible with a red error underneath.
        const message = formatUndoBlockMessage(impact);
        hideUndoForEntry(entry.id);
        setUndoPreview(null);
        setUndoError(null);
        showToast(message, "error");
        return;
      }
      setUndoPreview({ entry, impact });
    } catch (err) {
      setUndoPreview(null);
      setUndoError(
        err instanceof Error ? err.message : "โหลดผลกระทบการย้อนกลับไม่สำเร็จ",
      );
      showToast(
        err instanceof Error ? err.message : "โหลดผลกระทบการย้อนกลับไม่สำเร็จ",
        "error",
      );
    } finally {
      setUndoBusy(false);
      setUndoPreviewLoading(false);
    }
  };

  const handleConfirmUndo = async () => {
    if (!undoPreview) return;
    const owner = ownerUserId?.trim() || resolveOwnerUserId();
    if (!owner || !workspaceId?.trim()) {
      setUndoError("ไม่พบ workspace สำหรับย้อนกลับ");
      return;
    }
    if (undoPreview.impact.blocked || !undoPreview.impact.ok) {
      setUndoError(formatUndoBlockMessage(undoPreview.impact));
      return;
    }
    setUndoBusy(true);
    setUndoError(null);
    try {
      await postUndoCorrection({
        ownerUserId: owner,
        workspaceId: workspaceId.trim(),
        targetOperationId: undoPreview.entry.id,
      });
      // Hide undo UI immediately — don't wait for list/history refresh.
      hideUndoForEntry(undoPreview.entry.id);
      setUndoPreview(null);
      const refresh = Promise.resolve(onUndone?.());
      const rowsPromise = fetchPendingItemHistory(item, owner);
      await refresh;
      const rows = await rowsPromise;
      setEntries(rows);
      setLoadError(false);
    } catch (err) {
      setUndoError(err instanceof Error ? err.message : "ย้อนกลับไม่สำเร็จ");
    } finally {
      setUndoBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <CurrentSummaryCard item={item} />
        <div className="flex items-center justify-center rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) px-4 py-14">
          <p className="text-sm font-semibold text-aera-charcoal">กำลังโหลดประวัติ…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <CurrentSummaryCard item={item} />

      {loadError ? (
        <div className="flex items-center justify-center rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) px-4 py-14">
          <p className="text-sm font-semibold text-aera-charcoal">โหลดประวัติไม่สำเร็จ</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) px-4 py-14">
          <p className="text-sm font-semibold text-aera-charcoal">ยังไม่มีประวัติ</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-aera-white px-4 py-4">
          <h3 className="mb-3 text-sm font-bold text-aera-charcoal">ประวัติ</h3>
          {entries.map((entry, index) => {
            const canUndo =
              !readOnly &&
              !undoBlockedIds.has(entry.id) &&
              (entry.reversible === true ||
                Boolean(entry.availableActions?.includes("undo_latest")));
            return (
              <HistoryEntryRow
                key={entry.id}
                entry={entry}
                isLast={index === entries.length - 1}
                canUndo={canUndo}
                undoBusy={undoBusy || undoPreviewLoading}
                onRequestUndo={() => void handleRequestUndo(entry)}
              />
            );
          })}
        </div>
      )}

      <MoneyUndoConfirmSheet
        isOpen={undoPreview != null}
        onClose={() => {
          if (undoBusy) return;
          setUndoPreview(null);
        }}
        busy={undoBusy}
        title={
          undoPreview?.impact.title ||
          undoPreview?.entry.title ||
          "ย้อนกลับการแก้ไข"
        }
        detailLines={
          undoPreview
            ? buildReceivableUndoDetailLines(
                undoPreview.entry,
                undoPreview.impact,
              )
            : []
        }
        metaLine={
          undoPreview
            ? [
                undoPreview.entry.channelLabel ||
                  undoPreview.impact.channel_label,
                undoPreview.entry.occurredAt ||
                  undoPreview.impact.occurred_at,
              ]
                .filter(Boolean)
                .join(" · ") || null
            : null
        }
        error={undoError}
        blocked={Boolean(
          undoPreview?.impact.blocked ||
            (undoPreview && !undoPreview.impact.ok),
        )}
        onConfirm={() => void handleConfirmUndo()}
      />
    </div>
  );
}

function buildReceivableUndoDetailLines(
  entry: ReceivableHistoryEntry,
  impact: UndoImpact,
): string[] {
  const blocked = impact.blocked || !impact.ok;
  if (blocked) {
    return impact.summary_lines?.length
      ? impact.summary_lines
      : [formatUndoBlockMessage(impact)];
  }
  if (entry.details?.length) return entry.details;
  if (impact.details?.length) return impact.details;
  return (impact.summary_lines || []).filter(
    (line) => line !== entry.description && line !== impact.description,
  );
}
